import { DatePipe } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { GoliathCoreEnumerationTypeStore, KanbanCardBodyComponent } from '@digiwall/goliath-core';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { BasePlanningCardComponent } from '../base-planning-card.component';
import { PlanningDesiredDateComponent } from '../../planning-desired-date.component';
import { ServiceWorkOrderPlanningItemFieldsDto } from './service-work-order-planning-item-fields';

/**
 * Item card for the ServiceWorkOrder planning data source. Rendered by the board through
 * NgComponentOutlet with only the generic item dto; the ServiceWorkOrder specifics are read from the
 * typed `displayFields` payload. Content is fully custom (projected into `core-kanban-card-body`); the
 * wrapper supplies the shared reference/heading/status-dot chrome, resolving the status from the store's
 * workOrderStatus values + the item's `statusId` (the same colour the board draws as the side border).
 */
@Component({
  selector: 'document-planning-service-work-order-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [KanbanCardBodyComponent, LocalizationModule, PlanningDesiredDateComponent],
  template: `
    <core-kanban-card-body
      [reference]="fields().displayNumber"
      [heading]="fields().customer"
      [statusEnum]="store.workOrderStatus()"
      [status]="dto().statusId"
      [statusEditable]="statusEditable()"
      [selectableStatuses]="store.workOrderStatus()"
      [statusItemId]="statusItemId()"
    >
      @if (isVisible('articleCode') && fields().articleCode; as articleCode) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::Planning:CardField:ArticleCode' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ articleCode }}</span>
        </div>
      }
      @if (isVisible('article') && fields().article; as article) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ 'Doc::Article' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ article }}</span>
        </div>
      }
      @if (isVisible('articleSerial') && fields().articleSerial; as serial) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::StockDoc:ArticleSerial' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ serial }}</span>
        </div>
      }
      @if (isVisible('responsible') && fields().responsible; as responsible) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Responsible' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ responsible }}</span>
        </div>
      }
      @if (isVisible('schedule') && scheduleLabel(); as scheduleLabel) {
        <div class="kanban-card-body__line">{{ scheduleLabel }}</div>
      }
      @if ((isVisible('kind') && kind()) || dto().requestedDate) {
        <div class="kanban-card-body__line d-flex align-items-center gap-2">
          @if (isVisible('kind') && kind(); as kind) {
            <span
              class="kanban-card-body__badge align-self-center mt-0"
              [style.color]="kind.textColor || null"
              [style.background]="kind.backgroundColor || null"
            >
              {{ kind.name.current }}
            </span>
          }
          <document-planning-desired-date
            class="ms-auto"
            [date]="dto().requestedDate"
            [critical]="requestedDateCritical()"
          />
        </div>
      }
    </core-kanban-card-body>
  `,
})
export class ServiceWorkOrderPlanningCardComponent extends BasePlanningCardComponent<ServiceWorkOrderPlanningItemFieldsDto> {
  protected readonly store = inject(GoliathDocumentsEnumerationTypeStore);
  protected readonly coreStore = inject(GoliathCoreEnumerationTypeStore);
  private readonly datePipe = inject(DatePipe);
  protected readonly dataSource = PlanningDataSource.ServiceWorkOrder;

  protected readonly kind = computed(() => {
    const kindId = this.fields().kind;
    const workOrderKinds = this.coreStore.serviceWorkOrderKinds();
    return kindId ? workOrderKinds.find(entity => entity.id === kindId) : undefined;
  });

  protected readonly scheduleLabel = computed(() => {
    const fields = this.fields();
    const date = fields.forecastedDate
      ? (this.datePipe.transform(fields.forecastedDate, 'd MMM y') ?? undefined)
      : undefined;
    const duration = fields.plannedDurationHours ? `${fields.plannedDurationHours}h` : undefined;
    return [date, duration].filter(Boolean).join(' · ') || undefined;
  });
}
